import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { SectionCard } from '../ui/Detail';
import { Notice, StatusBadge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Skeleton } from '../ui/Skeleton';
import { ErrorState } from '../ui/QueryStates';
import { Field, field } from '../create/Field';
import { PAYMENT_METHOD_LABEL, money } from '../money';
import { IconMoney } from '../icons';

/**
 * What has been paid against this invoice, and giving some of it back.
 *
 * Refunds were specified in PRD-v1 (a full payment state machine: succeeded →
 * partially_refunded → refunded, "refund reopens balance") and built on the server, and
 * had no button anywhere — so the only way to reverse a payment was a database edit.
 *
 * A refund is not an undo. The payment row stays, the refund is its own record, and the
 * invoice balance reopens — because "they paid and we gave it back" is a different story
 * from "they never paid", and only one of them is true.
 *
 * Everything below counts against what is LEFT on the payment, never the payment total.
 * The box used to fill itself with the whole amount every time it opened — including on a
 * payment already half given back — so the very first thing the office saw was a number the
 * server would refuse. The server enforces the ceiling either way (services/payment.ts);
 * this makes the screen agree with it before the click rather than after.
 */

const PAYMENT_TONE: Record<string, 'success' | 'warn' | 'neutral' | 'danger'> = {
  succeeded: 'success',
  partially_refunded: 'warn',
  refunded: 'neutral',
  failed: 'danger',
  pending: 'neutral',
  disputed: 'danger',
};

/** What is still refundable on a payment. `refundedCents` comes from payment.list. */
const remainingOn = (p: { amountCents: number; refundedCents: number }) =>
  Math.max(0, p.amountCents - p.refundedCents);

export function InvoicePaymentsCard({ invoiceId }: { invoiceId: string }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const [refunding, setRefunding] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  const q = useQuery(trpc.payment.list.queryOptions());
  const refund = useMutation(
    trpc.payment.refund.mutationOptions({
      onSuccess: () => {
        setRefunding(null);
        setAmount('');
        setReason('');
        void qc.invalidateQueries();
      },
    }),
  );

  if (q.isPending) {
    return (
      <SectionCard title="Payments">
        <Skeleton className="h-16" />
      </SectionCard>
    );
  }
  if (q.isError) {
    return (
      <SectionCard title="Payments">
        <ErrorState icon={IconMoney} what="payments" onRetry={() => void q.refetch()} />
      </SectionCard>
    );
  }

  const mine = q.data.filter((p) => p.invoiceId === invoiceId);
  if (mine.length === 0) return null;

  const cents = Math.round(Number(amount.replace(/[$,\s]/g, '')) * 100);
  const target = mine.find((p) => p.id === refunding);
  const valid = Number.isFinite(cents) && cents > 0;
  const targetLeft = target ? remainingOn(target) : 0;
  const overRefund = Boolean(target) && valid && cents > targetLeft;

  return (
    <SectionCard title={`Payments (${mine.length})`}>
      {refund.error ? <Notice tone="danger">{refund.error.message}</Notice> : null}

      <ul className="divide-y divide-border text-sm">
        {mine.map((p) => (
          <li key={p.id} className="space-y-2 py-2 first:pt-0 last:pb-0">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div className="min-w-0">
                <p className="font-medium tabular-nums">
                  {money(p.amountCents)}
                  {p.tipCents > 0 ? (
                    <span className="ml-2 text-xs text-muted-foreground">
                      + {money(p.tipCents)} tip
                    </span>
                  ) : null}
                </p>
                <p className="text-xs text-muted-foreground">
                  {PAYMENT_METHOD_LABEL[p.method] ?? p.method}
                  {p.reference ? ` · ${p.reference}` : ''}
                  {/* receivedAt is nullable — an em-dash beats a fabricated date. */}
                  {p.receivedAt
                    ? ` · ${new Date(p.receivedAt).toLocaleDateString()}`
                    : ' · —'}
                </p>
                {/* Once anything has gone back, the payment amount alone stops being the
                    whole story — say both numbers rather than make anyone subtract. */}
                {p.refundedCents > 0 ? (
                  <p className="text-xs text-muted-foreground tabular-nums">
                    {money(p.refundedCents)} refunded
                    {remainingOn(p) > 0 ? ` · ${money(remainingOn(p))} left` : ''}
                  </p>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <StatusBadge tone={PAYMENT_TONE[p.status] ?? 'neutral'}>
                  {p.status.replace('_', ' ')}
                </StatusBadge>
                {(p.status === 'succeeded' || p.status === 'partially_refunded') &&
                remainingOn(p) > 0 ? (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setRefunding(p.id);
                      setAmount((remainingOn(p) / 100).toFixed(2));
                    }}
                  >
                    Refund
                  </Button>
                ) : null}
              </div>
            </div>

            {refunding === p.id ? (
              <div className="space-y-2 rounded-[var(--radius)] border border-border p-3">
                <div className="grid gap-2 sm:grid-cols-2">
                  <Field label={`How much back (up to ${money(remainingOn(p))})`}>
                    <input
                      className={field}
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      inputMode="decimal"
                      autoFocus
                    />
                  </Field>
                  <Field label="Why">
                    <input
                      className={field}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Overcharged for the part"
                    />
                  </Field>
                </div>
                {overRefund ? (
                  <Notice tone="warn">
                    {p.refundedCents > 0
                      ? `Only ${money(targetLeft)} is left on this payment — ${money(
                          p.refundedCents,
                        )} has already gone back.`
                      : `That's more than the ${money(p.amountCents)} they paid.`}
                  </Notice>
                ) : null}
                {/* Said before the click, because a refund moves real money and the
                    invoice going back to unpaid surprises anyone who didn't expect it. */}
                <Notice tone="warn">
                  This puts {money(valid ? cents : 0)} back to the customer and reopens
                  the balance on this invoice.
                </Notice>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="danger"
                    disabled={!valid || overRefund || refund.isPending}
                    onClick={() =>
                      refund.mutate({
                        paymentId: p.id,
                        amountCents: cents,
                        reason: reason.trim() || undefined,
                        ...idem(),
                      })
                    }
                  >
                    {refund.isPending
                      ? 'Refunding…'
                      : `Refund ${money(valid ? cents : 0)}`}
                  </Button>
                  <Button variant="ghost" onClick={() => setRefunding(null)}>
                    Never mind
                  </Button>
                </div>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}
